import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Container, Title, Text, Stack, Group, Badge, Button, Paper,
  TextInput, Textarea, Modal, Skeleton, Center, Alert,
  ActionIcon, Tooltip, Select, Table, Anchor, Collapse,
  Box, ScrollArea, Loader,
} from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import {
  IconAddressBook, IconPlus, IconPencil, IconTrash, IconSearch,
  IconPhone, IconMail, IconBuilding, IconAlertCircle, IconLock,
  IconChevronDown, IconChevronUp,
} from '@tabler/icons-react';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { api } from '../lib/api';
import { CallButton } from '../components/call/CallButton';
import { customerLabel } from '../lib/weclappCustomer';
import type { ContactDirectoryEntry, ContactDirectoryWriteInput, ContactDirectoryPatchInput, CustomerType } from '../lib/api/domains/contactDirectory';

// Read-only CustomerContact-Einträge: Badge spiegelt den echten Ursprung wider,
// statt alles pauschal "Portal-Benutzer" zu nennen.
// Hinweis: caller-Einträge (Anrufer-Triage) sind als einzige read-only-Variante pflegbar.
const CONTACT_KIND_BADGE: Record<
  'portal' | 'synced' | 'caller',
  { label: string; color: string; tip: string }
> = {
  portal: { label: 'Portal', color: 'blue', tip: 'Portal-Benutzer · Login aktiv (schreibgeschützt)' },
  synced: { label: 'Synchronisiert', color: 'gray', tip: 'Aus weclapp synchronisiert · Änderungen werden zu weclapp übertragen' },
  caller: { label: 'Anrufer', color: 'grape', tip: 'Aus Anrufer-Triage · bearbeitbar' },
};

// Kategorie eines Kontakts = CustomerType seiner Firma (dieselbe Klassifizierung
// wie in der Anrufer-Triage: Kunde / Interessent / Lieferant / Partner).
const CUSTOMER_TYPE_BADGE: Record<CustomerType, { label: string; color: string }> = {
  CUSTOMER: { label: 'Kunde', color: 'teal' },
  SALES_LEAD: { label: 'Interessent', color: 'blue' },
  SUPPLIER: { label: 'Lieferant', color: 'orange' },
  PARTNER: { label: 'Partner', color: 'violet' },
};

const CUSTOMER_TYPE_OPTIONS: Array<{ value: CustomerType; label: string }> = [
  { value: 'CUSTOMER', label: 'Kunde' },
  { value: 'SALES_LEAD', label: 'Interessent (Sales-Lead)' },
  { value: 'SUPPLIER', label: 'Lieferant' },
  { value: 'PARTNER', label: 'Partner' },
];

// ─── Edit/Create Modal ────────────────────────────────────────────────────────

interface ContactFormState {
  name: string;
  email: string;
  phone: string;
  department: string;
  position: string;
  notes: string;
  customerId: string;
  customerType: CustomerType | '';
}

const EMPTY_FORM: ContactFormState = {
  name: '', email: '', phone: '', department: '', position: '', notes: '', customerId: '', customerType: '',
};

function ContactModal({
  opened,
  onClose,
  entry,
  customers,
}: {
  opened: boolean;
  onClose: () => void;
  entry: ContactDirectoryEntry | null;
  customers: Array<{ value: string; label: string }>;
}) {
  const qc = useQueryClient();
  const isEdit = entry !== null;
  // CustomerContact-Einträge (Anrufer/synchronisiert) haben keine Abteilung/Position/Notizen.
  const isContactEntry = entry?.kind === 'caller' || entry?.kind === 'synced';
  // Synchronisierte Kontakte: Änderungen werden nach weclapp zurückgeschrieben.
  const isSynced = entry?.kind === 'synced';

  const [form, setForm] = useState<ContactFormState>(() =>
    entry
      ? {
          name: entry.name,
          email: entry.email ?? '',
          phone: entry.phone ?? '',
          department: entry.department ?? '',
          position: entry.position ?? '',
          notes: entry.notes ?? '',
          customerId: entry.customerId ?? '',
          customerType: entry.customerType ?? '',
        }
      : EMPTY_FORM
  );

  // Das Modal bleibt dauerhaft gemountet, daher läuft der useState-Initializer
  // nur einmal (mit entry=null). Beim Öffnen für einen Eintrag muss das Formular
  // hier neu aus dem entry befüllt werden – sonst bleiben alle Felder leer.
  useEffect(() => {
    if (!opened) return;
    setForm(
      entry
        ? {
            name: entry.name,
            email: entry.email ?? '',
            phone: entry.phone ?? '',
            department: entry.department ?? '',
            position: entry.position ?? '',
            notes: entry.notes ?? '',
            customerId: entry.customerId ?? '',
            customerType: entry.customerType ?? '',
          }
        : EMPTY_FORM
    );
  }, [opened, entry]);

  const set = (field: keyof ContactFormState) => (val: string | null) =>
    setForm((f) => ({ ...f, [field]: val ?? '' }));

  const createMutation = useMutation({
    mutationFn: (data: ContactDirectoryWriteInput) => api.createDirectoryContact(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['contactDirectory'] });
      notifications.show({ color: 'teal', title: 'Kontakt angelegt', message: form.name });
      onClose();
    },
    onError: () => notifications.show({ color: 'red', title: 'Fehler', message: 'Kontakt konnte nicht angelegt werden.' }),
  });

  const updateMutation = useMutation({
    mutationFn: (data: ContactDirectoryPatchInput) => api.updateContact(entry!.id, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['contactDirectory'] });
      notifications.show({ color: 'teal', title: 'Kontakt aktualisiert', message: form.name });
      onClose();
    },
    onError: (err) =>
      notifications.show({
        color: 'red',
        title: 'Fehler',
        message: err instanceof Error && err.message ? err.message : 'Kontakt konnte nicht gespeichert werden.',
      }),
  });

  const loading = createMutation.isPending || updateMutation.isPending;

  function handleSubmit() {
    if (!form.name.trim()) {
      notifications.show({ color: 'orange', message: 'Name ist erforderlich.' });
      return;
    }
    if (isEdit) {
      updateMutation.mutate({
        name: form.name.trim() || undefined,
        email: form.email.trim() || null,
        phone: form.phone.trim() || null,
        // CustomerContact-Einträge haben keine Abteilung/Position/Notizen.
        ...(isContactEntry
          ? {}
          : {
              department: form.department.trim() || null,
              position: form.position.trim() || null,
              notes: form.notes.trim() || null,
            }),
        ...(form.customerType ? { customerType: form.customerType } : {}),
      });
    } else {
      if (!form.customerId) {
        notifications.show({ color: 'orange', message: 'Bitte einen Kunden auswählen.' });
        return;
      }
      createMutation.mutate({
        name: form.name.trim(),
        email: form.email.trim() || null,
        phone: form.phone.trim() || null,
        department: form.department.trim() || null,
        position: form.position.trim() || null,
        notes: form.notes.trim() || null,
        customerId: form.customerId,
      });
    }
  }

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={isEdit ? 'Kontakt bearbeiten' : 'Neuen Kontakt anlegen'}
      size="md"
    >
      <Stack gap="sm">
        {isSynced && (
          <Alert color="blue" variant="light" icon={<IconAlertCircle size={16} />}>
            Dieser Kontakt ist mit weclapp synchronisiert. Änderungen an Name, Telefon
            und E-Mail werden direkt zu weclapp übertragen.
          </Alert>
        )}
        <TextInput
          label="Name"
          required
          value={form.name}
          onChange={(e) => set('name')(e.currentTarget.value)}
          placeholder="Max Mustermann"
        />
        {!isEdit && (
          <Select
            label="Kunde"
            required
            value={form.customerId || null}
            onChange={set('customerId')}
            data={customers}
            searchable
            placeholder="Kunde auswählen…"
          />
        )}
        <TextInput
          label="Telefon"
          value={form.phone}
          onChange={(e) => set('phone')(e.currentTarget.value)}
          placeholder="+49 231 123456"
          leftSection={<IconPhone size={14} />}
        />
        <TextInput
          label="E-Mail"
          value={form.email}
          onChange={(e) => set('email')(e.currentTarget.value)}
          placeholder="max@praxis.de"
          leftSection={<IconMail size={14} />}
        />
        {isEdit && (
          <Select
            label="Kategorie"
            description="Gilt für die ganze Firma (CRM-Kategorie)"
            value={form.customerType || null}
            onChange={set('customerType')}
            data={CUSTOMER_TYPE_OPTIONS}
            clearable
            placeholder="Keine Kategorie"
          />
        )}
        {!isContactEntry && (
          <>
            <Group grow>
              <TextInput
                label="Abteilung"
                value={form.department}
                onChange={(e) => set('department')(e.currentTarget.value)}
              />
              <TextInput
                label="Position"
                value={form.position}
                onChange={(e) => set('position')(e.currentTarget.value)}
              />
            </Group>
            <Textarea
              label="Notizen"
              value={form.notes}
              onChange={(e) => set('notes')(e.currentTarget.value)}
              rows={3}
            />
          </>
        )}
        <Group justify="flex-end" mt="xs">
          <Button variant="subtle" onClick={onClose} disabled={loading}>Abbrechen</Button>
          <Button onClick={handleSubmit} loading={loading}>
            {isEdit ? 'Speichern' : 'Anlegen'}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export function ContactDirectoryPage() {
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [debouncedSearch] = useDebouncedValue(search, 300);
  const [customerFilter, setCustomerFilter] = useState<string | null>(null);
  const [categoryFilter, setCategoryFilter] = useState<CustomerType | null>(null);
  const [modalEntry, setModalEntry] = useState<ContactDirectoryEntry | null | 'new'>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<ContactDirectoryEntry | null>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);

  const { data, isLoading, isError, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteQuery({
      queryKey: ['contactDirectory', debouncedSearch, customerFilter, categoryFilter],
      queryFn: ({ pageParam }) =>
        api.listContacts({
          q: debouncedSearch || undefined,
          customerId: customerFilter || undefined,
          customerType: categoryFilter || undefined,
          page: pageParam,
          limit: 50,
        }),
      initialPageParam: 1,
      getNextPageParam: (lastPage, allPages) =>
        lastPage.hasMore ? allPages.length + 1 : undefined,
      staleTime: 30_000,
    });

  // Infinite Scroll: lädt die nächste Seite, sobald der Sentinel am Listenende
  // in den Viewport kommt.
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && hasNextPage && !isFetchingNextPage) {
          void fetchNextPage();
        }
      },
      { rootMargin: '200px' }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const { data: customersData } = useQuery({
    queryKey: ['adminCustomers', ''],
    queryFn: () => api.listCustomers({ limit: 200 }),
    staleTime: 60_000,
  });

  const customers: Array<{ value: string; label: string }> =
    (customersData?.items ?? []).map((c: { id: string; name: string; customerNumber: string | null }) => ({
      value: c.id,
      label: customerLabel(c.name, c.customerNumber),
    }));

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.deleteContact(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['contactDirectory'] });
      notifications.show({ color: 'teal', title: 'Kontakt gelöscht', message: '' });
      setDeleteConfirm(null);
    },
    onError: () => notifications.show({ color: 'red', title: 'Fehler', message: 'Löschen fehlgeschlagen.' }),
  });

  const items = useMemo(() => data?.pages.flatMap((p) => p.items) ?? [], [data]);
  const total = data?.pages[0]?.total ?? 0;
  const withPhone = items.filter((e) => e.phone);

  return (
    <Container size="xl" py="md">
      <Stack gap="md">
        <Group justify="space-between" wrap="nowrap">
          <Group gap="xs">
            <IconAddressBook size={24} />
            <Title order={2}>Kontaktverzeichnis</Title>
          </Group>
          <Button
            leftSection={<IconPlus size={14} />}
            onClick={() => setModalEntry('new')}
          >
            Kontakt hinzufügen
          </Button>
        </Group>

        {total > 0 && (
          <Text size="sm" c="dimmed">
            {items.length} von {total} Kontakten geladen · {withPhone.length} mit Telefonnummer
          </Text>
        )}

        <Group gap="sm" wrap="nowrap">
          <TextInput
            flex={1}
            placeholder="Suche nach Name, Telefon, E-Mail…"
            leftSection={<IconSearch size={14} />}
            value={search}
            onChange={(e) => setSearch(e.currentTarget.value)}
          />
          <Select
            w={180}
            placeholder="Alle Kategorien"
            data={CUSTOMER_TYPE_OPTIONS}
            value={categoryFilter}
            onChange={(v) => setCategoryFilter((v as CustomerType) || null)}
            clearable
          />
          <Select
            w={220}
            placeholder="Alle Kunden"
            data={customers}
            value={customerFilter}
            onChange={setCustomerFilter}
            searchable
            clearable
            leftSection={<IconBuilding size={14} />}
          />
        </Group>

        {isError && (
          <Alert color="red" icon={<IconAlertCircle size={16} />}>
            Fehler beim Laden des Kontaktverzeichnisses.
          </Alert>
        )}

        {isLoading ? (
          <Stack gap="xs">
            {[...Array(5)].map((_, i) => <Skeleton key={i} h={44} radius="sm" />)}
          </Stack>
        ) : items.length === 0 ? (
          <Center py="xl">
            <Stack align="center" gap="xs">
              <IconAddressBook size={40} color="gray" />
              <Text c="dimmed">Keine Kontakte gefunden</Text>
            </Stack>
          </Center>
        ) : (
          <Paper withBorder radius="sm">
            <ScrollArea>
            <Table striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Name</Table.Th>
                  <Table.Th>Firma</Table.Th>
                  <Table.Th>Kategorie</Table.Th>
                  <Table.Th>Telefon</Table.Th>
                  <Table.Th>E-Mail</Table.Th>
                  <Table.Th>Position</Table.Th>
                  <Table.Th w={80}></Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {items.map((entry) => (
                  <Table.Tr key={entry.id}>
                    <Table.Td>
                      <Group gap={6} wrap="nowrap">
                        <Text size="sm" fw={500}>{entry.name}</Text>
                        {entry.isPortalUser && (() => {
                          const meta =
                            entry.kind && entry.kind !== 'employee'
                              ? CONTACT_KIND_BADGE[entry.kind]
                              : CONTACT_KIND_BADGE.synced;
                          return (
                            <Tooltip label={meta.tip} position="top">
                              <Badge
                                size="xs"
                                color={meta.color}
                                variant="light"
                                leftSection={<IconLock size={10} />}
                              >
                                {meta.label}
                              </Badge>
                            </Tooltip>
                          );
                        })()}
                        {!entry.isPortalUser && !entry.phone && (
                          <Badge size="xs" color="yellow" variant="light">Kein Telefon</Badge>
                        )}
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" c="dimmed">{entry.customer?.name ?? '–'}</Text>
                    </Table.Td>
                    <Table.Td>
                      {entry.customerType ? (
                        <Badge
                          size="sm"
                          variant="light"
                          color={CUSTOMER_TYPE_BADGE[entry.customerType].color}
                        >
                          {CUSTOMER_TYPE_BADGE[entry.customerType].label}
                        </Badge>
                      ) : (
                        <Text size="sm" c="dimmed">–</Text>
                      )}
                    </Table.Td>
                    <Table.Td>
                      {entry.phone ? (
                        <Group gap={6} wrap="nowrap">
                          <Text size="sm">{entry.phone}</Text>
                          <CallButton
                            phone={entry.phone}
                            contactName={entry.name}
                            customerId={entry.customerId}
                            originType="directory"
                            originId={entry.id}
                            size="xs"
                          />
                        </Group>
                      ) : (
                        <Text size="sm" c="dimmed">–</Text>
                      )}
                    </Table.Td>
                    <Table.Td>
                      {entry.email ? (
                        <Anchor href={`mailto:${entry.email}`} size="sm">
                          {entry.email}
                        </Anchor>
                      ) : (
                        <Text size="sm" c="dimmed">–</Text>
                      )}
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" c="dimmed">
                        {[entry.position, entry.department].filter(Boolean).join(' · ') || '–'}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      {(() => {
                        // Bearbeiten: Mitarbeiter, Anrufer und – mit weclapp-Write-back –
                        // synchronisierte Kontakte.
                        const canEdit =
                          !entry.isPortalUser || entry.kind === 'caller' || entry.kind === 'synced';
                        // Löschen: nur lokal verwaltete Einträge — synchronisierte würde der
                        // Sync ohnehin neu anlegen.
                        const canDelete = !entry.isPortalUser || entry.kind === 'caller';
                        if (!canEdit && !canDelete) return null;
                        return (
                        <Group gap={4} wrap="nowrap">
                          {canEdit && (
                          <Tooltip label="Bearbeiten">
                            <ActionIcon
                              size="sm"
                              variant="subtle"
                              onClick={() => setModalEntry(entry)}
                            >
                              <IconPencil size={14} />
                            </ActionIcon>
                          </Tooltip>
                          )}
                          {canDelete && (
                          <Tooltip label="Löschen">
                            <ActionIcon
                              size="sm"
                              variant="subtle"
                              color="red"
                              onClick={() => setDeleteConfirm(entry)}
                            >
                              <IconTrash size={14} />
                            </ActionIcon>
                          </Tooltip>
                          )}
                        </Group>
                        );
                      })()}
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
            </ScrollArea>
            {/* Sentinel + Lade-Indikator für Infinite Scroll */}
            <div ref={sentinelRef} style={{ height: 1 }} />
            {isFetchingNextPage && (
              <Center py="sm">
                <Loader size="sm" />
              </Center>
            )}
          </Paper>
        )}
      </Stack>

      {/* Create/Edit Modal */}
      <ContactModal
        opened={modalEntry !== null}
        onClose={() => setModalEntry(null)}
        entry={modalEntry === 'new' ? null : (modalEntry ?? null)}
        customers={customers}
      />

      {/* Delete Confirm Modal */}
      <Modal
        opened={deleteConfirm !== null}
        onClose={() => setDeleteConfirm(null)}
        title="Kontakt löschen"
        size="sm"
      >
        <Stack gap="md">
          <Text size="sm">
            Soll <strong>{deleteConfirm?.name}</strong> wirklich gelöscht werden?
          </Text>
          <Group justify="flex-end">
            <Button variant="subtle" onClick={() => setDeleteConfirm(null)}>Abbrechen</Button>
            <Button
              color="red"
              loading={deleteMutation.isPending}
              onClick={() => deleteConfirm && deleteMutation.mutate(deleteConfirm.id)}
            >
              Löschen
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Container>
  );
}
